import isElectron from 'is-electron';

import Actions from 'flow/actions';
import appConfig from 'app-config';

import { ToneGenerator } from 'resources/tone-generator';
import {
    filterMediaDevicesBySpecificKind,
    getConnectedMediaDeviceOfKindWithId,
    setOutputDeviceWithIdToElement,
    getConnectedMediaDevices,
    getMediaDevicesLogString,
    getNameOfPrimaryAudioOutputDevice,
    getNameOfPrimaryAudioInputDevice,
} from 'flow/utils/media-utils';
import {
    saveHeadsetEnabledToStorage,
    getHeadsetEnabledFromStorage,
    getPreferredAudioOutputId,
    getPreferredRingtoneAudioOutputId,
    getPreferredMicrophoneId,
    getPreferredVideoInputId,
    savePreferredAudioOutputId,
    savePreferredRingtoneAudioOutputId,
    savePreferredMicrophoneId,
    savePreferredVideoInputId,
    getNoiseSuppressionEngine,
    saveNoiseSuppressionEngine,
    saveAudioDeviceDataSentForStatistics,
    getAudioDeviceDataSentForStatistics,
    addDeviceLogEntry,
    addHeadsetLogEntry,
    isChromeOrElectron,
} from 'flow/utils/browser-utils';
import { selectIsHeadsetControlEnabled } from 'flow/selectors/headset-selectors';
import { isNoiseSuppressionEngine } from 'flow/utils/noise-suppression-utils';
import { MainAudioContext } from 'resources/audio/wrapped-audio-context';
import { isAppCti } from 'flow/utils/app-utils';
import { electronActions } from 'flow/utils/electron-utils';
import { getLoggedInUserExtensionStatus } from 'flow/selectors/extension-selectors';
import { getLocalCallIds } from 'flow/selectors/call-selectors';
import {
    areHeadsetsEnabledForCallId,
    areHeadsetsDisabledBecauseOfMultipleCalls,
} from 'flow/selectors/headset-selectors';

export const registerHeadsetRef = (headsetRef) => ({
    type: Actions.REGISTER_HEADSET_REF,
    headsetRef,
});

export const unregisterHeadset = (headsetId) => ({
    type: Actions.UNREGISTER_HEADSET_REF,
    headsetId,
});

export const enableHeadsets = {
    type: Actions.ENABLE_HEADSETS,
};

export const disableHeadsets = {
    type: Actions.DISABLE_HEADSETS,
};

export const loadHeadsetConf = (extensionKey) => (dispatch) => {
    const headsetEnabled = getHeadsetEnabledFromStorage(extensionKey);
    // If it not electron or if the user has explicitly set the toggler to
    // 'false', then we disable the headsets functionality.
    //
    // The reason why we don't enable it for the non electron webapp is
    // because the Jabra integration does not support controlling multiple
    // apps at the same time. Having two instances (e.g. two tabs) running
    // at the same time while ansering an incoming call with the headset
    // will result in one instance answering the call and the other instance
    // hanging up the call. We chose to be consistant and turn off the
    // headset controls for the non electron webapp for all integrations,
    // even though Plantronics can handle this problem.
    if (!isElectron() || headsetEnabled === false) {
        dispatch(disableHeadsets);
        saveHeadsetEnabledToStorage(extensionKey, false);
    }
};

export const hsIncomingCall = (callId) => (_dispatch, getState) => {
    const state = getState();
    const { headsets } = state;

    if (!areHeadsetsEnabledForCallId(state, callId)) {
        return;
    }

    const registeredHeadsets = headsets.get('registeredHeadsets');
    registeredHeadsets.forEach((headset) => headset.incomingCall(callId));
};

export const hsOutgoingCall = (callId) => (_dispatch, getState) => {
    const state = getState();
    const { headsets } = state;

    if (!areHeadsetsEnabledForCallId(state, callId)) {
        return;
    }

    const registeredHeadsets = headsets.get('registeredHeadsets');
    registeredHeadsets.forEach((headset) => headset.outgoingCall(callId));
};

export const hsConfirmCall =
    (callId, incoming, answeredThroughHeadset) => (_dispatch, getState) => {
        const state = getState();
        const { headsets } = state;

        if (!areHeadsetsEnabledForCallId(state, callId)) {
            return;
        }

        const registeredHeadsets = headsets.get('registeredHeadsets');
        registeredHeadsets.forEach((headset) =>
            headset.confirmCall(callId, incoming, answeredThroughHeadset),
        );
    };
export const hsEndCall =
    (callId, hungupThroughHeadset) => (_dispatch, getState) => {
        const state = getState();
        const { headsets } = state;

        if (!areHeadsetsEnabledForCallId(state, callId)) {
            return;
        }

        const registeredHeadsets = headsets.get('registeredHeadsets');
        registeredHeadsets.forEach((headset) =>
            headset.endCall(callId, hungupThroughHeadset),
        );
    };

export const hsMute = (callId) => (_dispatch, getState) => {
    const { headsets } = getState();
    const registeredHeadsets = headsets.get('registeredHeadsets');
    registeredHeadsets.forEach((headset) => headset.muteCall(callId));
};

export const hsUnmute = (callId) => (_dispatch, getState) => {
    const { headsets } = getState();
    const registeredHeadsets = headsets.get('registeredHeadsets');
    registeredHeadsets.forEach((headset) => headset.unmuteCall(callId));
};

export const hsHoldCall = (callId) => (_dispatch, getState) => {
    const state = getState();
    const { headsets } = state;

    if (!areHeadsetsEnabledForCallId(state, callId)) {
        return;
    }

    const registeredHeadsets = headsets.get('registeredHeadsets');
    registeredHeadsets.forEach((headset) => headset.holdCall(callId));
};

export const hsUnholdCall = (callId) => (_dispatch, getState) => {
    const state = getState();
    const { headsets } = state;

    if (!areHeadsetsEnabledForCallId(state, callId)) {
        return;
    }

    const registeredHeadsets = headsets.get('registeredHeadsets');
    registeredHeadsets.forEach((headset) => headset.resumeCall(callId));
};

export const hsUserUnavailable = () => (_dispatch, getState) => {
    const { headsets } = getState();
    const registeredHeadsets = headsets.get('registeredHeadsets');
    registeredHeadsets.forEach((headset) => headset.userUnavailable());
};

export const hsUserAvailable = () => (_dispatch, getState) => {
    const { headsets } = getState();
    const registeredHeadsets = headsets.get('registeredHeadsets');
    registeredHeadsets.forEach((headset) => headset.userAvailable());
};

/**
 * Because multiple calls are not handled very well by some headset vendors
 * (looking at you Jabra), we temporarily turn off headset support while
 * there are multiple calls. Once all calls are hung up, we enable headset
 * support again.
 *
 * While temporarily disabled, we still send updates to the headset for the
 * first call to allow it to keep up to date.
 */
export const hsMultipleCalls = () => (dispatch, getState) => {
    const state = getState();
    const localCallIds = getLocalCallIds(state);

    if (areHeadsetsDisabledBecauseOfMultipleCalls(state) || !localCallIds) {
        return;
    }

    const currentCallId = localCallIds[0];
    addHeadsetLogEntry(
        'Disabling headset integrations for new calls because of multiple calls, enabling when all calls are ended',
    );

    dispatch({
        type: Actions.DISABLE_HEADSETS_WHEN_MULTIPLE_CALLS,
        exceptionCallId: currentCallId,
    });
};

export const hsEndLastCall = () => (dispatch, getState) => {
    const headsetsDisabled =
        areHeadsetsDisabledBecauseOfMultipleCalls(getState());

    if (!headsetsDisabled) {
        return;
    }

    addHeadsetLogEntry(
        'Enabling headset integrations after being temporarily disabled because of multiple calls',
    );

    dispatch({
        type: Actions.ENABLE_HEADSETS_AFTER_MULTIPLE_CALLS,
    });
};

//
// PREFERRED MICROPHONE
//

export const setPreferredMicrophoneId = (microphoneId) => (dispatch) => {
    savePreferredMicrophoneId(microphoneId);
    dispatch(receivePreferredMicrophoneId(microphoneId));
    addDeviceLogEntry('Preferred input device id changed');
    dispatch(logConnectedInputDevices);
};

export const loadPreferredMicrophoneId = (dispatch) => {
    const preferredMicrophoneId = getPreferredMicrophoneId();
    dispatch(receivePreferredMicrophoneId(preferredMicrophoneId));
};

export const receivePreferredMicrophoneId = (preferredMicId) => ({
    type: Actions.DEVICES_PREFERRED_MICROPHONE_ID,
    preferredMicId,
});

//
// NOISE SUPPRESSION
//

export const setNoiseSuppressionEngine = (engine) => (dispatch) => {
    saveNoiseSuppressionEngine(engine);
    dispatch(receiveNoiseSuppressionEngine(engine));
    addDeviceLogEntry(`Noise suppression engine changed to ${engine}`);
};

export const loadNoiseSuppressionEngine = (dispatch) => {
    const stored = getNoiseSuppressionEngine();
    dispatch(
        receiveNoiseSuppressionEngine(
            isNoiseSuppressionEngine(stored) ? stored : 'off',
        ),
    );
};

export const receiveNoiseSuppressionEngine = (noiseSuppressionEngine) => ({
    type: Actions.DEVICES_NOISE_SUPPRESSION_ENGINE,
    noiseSuppressionEngine,
});

//
// PREFERRED AUDIO OUTPUT
//

export const setPreferredAudioOutputId =
    (audioOutputId) => (dispatch, getState) => {
        const { phone, headsets } = getState();
        savePreferredAudioOutputId(audioOutputId);
        dispatch(receivePreferredAudioOutputId(audioOutputId));
        addDeviceLogEntry('Preferred output device id changed');
        dispatch(logConnectedOutputDevices);

        const audioReference = phone.get('audioReference');
        const mediaDevices = headsets.get('connectedMediaDevices');
        const audioOutput = getConnectedMediaDeviceOfKindWithId(
            mediaDevices,
            'audiooutput',
            audioOutputId,
        );

        const preferredAdditionalRingtoneAudioOutputId = headsets.get(
            'preferredRingtoneAudioOutputId',
        );

        if (audioOutput) {
            ToneGenerator.setOutputDevice(audioOutput.deviceId);
            setOutputDeviceWithIdToElement(
                audioOutput.deviceId,
                audioReference,
            );
            MainAudioContext.getPrimaryOutput().setOutputDevice(
                audioOutput.deviceId,
            );
        }

        // This is overkill. This should be handled once. Search for this comment for dubes.
        if (!preferredAdditionalRingtoneAudioOutputId) {
            MainAudioContext.getSecondaryOutput().deactivate();
        } else if (preferredAdditionalRingtoneAudioOutputId === audioOutputId) {
            MainAudioContext.getSecondaryOutput().deactivate();
        } else {
            MainAudioContext.getSecondaryOutput().activate();
        }
    };

export const loadPreferredAudioOutputId = (dispatch) => {
    const preferredAudioOutputId = getPreferredAudioOutputId();
    dispatch(receivePreferredAudioOutputId(preferredAudioOutputId));
};

export const receivePreferredAudioOutputId = (preferredAudioOutputId) => ({
    type: Actions.DEVICES_PREFERRED_AUDIO_OUTPUT_ID,
    preferredAudioOutputId,
});

//
// PREFERRED RINGTONE AUDIO OUTPUT
//

export const setPreferredRingtoneAudioOutputId =
    (preferredAdditionalRingtoneAudioOutputId) => (dispatch, getState) => {
        const { headsets } = getState();

        savePreferredRingtoneAudioOutputId(
            preferredAdditionalRingtoneAudioOutputId,
        );
        dispatch(
            receivePreferredRingtoneAudioOutputId(
                preferredAdditionalRingtoneAudioOutputId,
            ),
        );
        addDeviceLogEntry('Additional ringtone device id changed');
        dispatch(logConnectedOutputDevices);

        const mediaDevices = headsets.get('connectedMediaDevices');
        const additionalRingtoneAudioOutput =
            getConnectedMediaDeviceOfKindWithId(
                mediaDevices,
                'audiooutput',
                preferredAdditionalRingtoneAudioOutputId,
            );

        const preferredAudioOutputId = headsets.get('preferredAudioOutputId');

        if (additionalRingtoneAudioOutput) {
            MainAudioContext.getSecondaryOutput().setOutputDevice(
                additionalRingtoneAudioOutput.deviceId,
            );
        }

        // This is overkill. This should be handled once. Search for this comment for dubes.
        if (!additionalRingtoneAudioOutput) {
            MainAudioContext.getSecondaryOutput().deactivate();
        } else if (
            preferredAdditionalRingtoneAudioOutputId === preferredAudioOutputId
        ) {
            MainAudioContext.getSecondaryOutput().deactivate();
        } else {
            MainAudioContext.getSecondaryOutput().activate();
        }
    };

export const loadPreferredRingtoneAudioOutputId = (dispatch) => {
    const preferredRingtoneAudioOutputId = getPreferredRingtoneAudioOutputId();
    dispatch(
        receivePreferredRingtoneAudioOutputId(preferredRingtoneAudioOutputId),
    );
};

export const receivePreferredRingtoneAudioOutputId = (
    preferredRingtoneAudioOutputId,
) => ({
    type: Actions.DEVICES_PREFERRED_RINGTONE_AUDIO_OUTPUT_ID,
    preferredRingtoneAudioOutputId,
});

//
// PREFERRED VIDEO INPUT
//

export const setPreferredVideoInputId = (videoInputId) => (dispatch) => {
    savePreferredVideoInputId(videoInputId);
    dispatch(receivePreferredVideoInputId(videoInputId));
};

export const loadPreferredVideoInputId = (dispatch) => {
    const preferredVideoInputId = getPreferredVideoInputId();
    dispatch(receivePreferredVideoInputId(preferredVideoInputId));
};

export const receivePreferredVideoInputId = (preferredVideoInputId) => ({
    type: Actions.DEVICES_PREFERRED_VIDEO_INPUT_ID,
    preferredVideoInputId,
});

export const logAudioDeviceDataIfAppropriate = () => (dispatch, getState) => {
    getConnectedMediaDevices().then((mediaDevices) => {
        const audioOutputDeviceName = dispatch(
            getNameOfPrimaryAudioOutputDevice(mediaDevices),
        );
        const audioInputDeviceName = dispatch(
            getNameOfPrimaryAudioInputDevice(mediaDevices),
        );
        const headsetControlsEnabled =
            selectIsHeadsetControlEnabled(getState());
        const dateNow = Date.now();
        const clientType = isElectron() ? 'Desktop' : 'Web';
        const appVersion = appConfig.version;

        const previouslySavedDeviceData = getAudioDeviceDataSentForStatistics();

        let shouldSendData = true;

        if (previouslySavedDeviceData) {
            const previouslySavedAudioOutputDeviceName =
                previouslySavedDeviceData.audioOutputDeviceName;
            const previouslySavedAudioInputDeviceName =
                previouslySavedDeviceData.audioInputDeviceName;
            const previouslySavedHeadsetControlsEnabled =
                previouslySavedDeviceData.headsetControlsEnabled;
            const previouslySavedDClient = previouslySavedDeviceData.clientType;
            const previouslySavedAppVersion =
                previouslySavedDeviceData.appVersion;
            const previouslySavedDate = previouslySavedDeviceData.saveDateTime;

            //Somewhat arbitrarily chosen time threshold
            const lastLogIsNewerThanTwoWeeks =
                !dateOneIsMoreThanFourteenDaysOlderThanDateTwo(
                    previouslySavedDate,
                    dateNow,
                );

            if (
                appVersion === previouslySavedAppVersion &&
                audioOutputDeviceName ===
                    previouslySavedAudioOutputDeviceName &&
                audioInputDeviceName === previouslySavedAudioInputDeviceName &&
                headsetControlsEnabled ===
                    previouslySavedHeadsetControlsEnabled &&
                clientType === previouslySavedDClient &&
                lastLogIsNewerThanTwoWeeks
            ) {
                shouldSendData = false;
            }
        }

        if (!shouldSendData) {
            return;
        }

        const deviceData = {
            audioOutputDeviceName: audioOutputDeviceName,
            audioInputDeviceName: audioInputDeviceName,
            headsetControlsEnabled: headsetControlsEnabled,
            clientType: clientType,
            saveDateTime: dateNow,
            appVersion: appVersion,
        };

        saveAudioDeviceDataSentForStatistics(deviceData);

        //Needs to be stringified or it'll be displayed as 'Headset data: [object Object]' in the logs
        const deviceDataString = JSON.stringify(deviceData);
        addDeviceLogEntry(`Headset device data: \n${deviceDataString}`);
    });
};

const dateOneIsMoreThanFourteenDaysOlderThanDateTwo = (dateOne, dateTwo) => {
    const fourteenDaysInMilliseconds = 1209600000;
    const timeDiff = dateTwo - dateOne;
    return timeDiff > fourteenDaysInMilliseconds;
};

//
// CONNECTED MEDIA DEVICES
//

export const fetchConnectedMediaDevices = (dispatch, getState) => {
    if (!isChromeOrElectron() && !isAppCti(getState())) {
        return;
    }

    getConnectedMediaDevices().then(
        (devices) => {
            dispatch(setConnectedMediaDevices(devices));
            dispatch(logConnectedOutputDevices);
            dispatch(logConnectedInputDevices);
        },
        (/*error*/) => {
            dispatch(setConnectedMediaDevices(null));
            dispatch(logConnectedOutputDevices);
            dispatch(logConnectedInputDevices);
        },
    );
};

export const setConnectedMediaDevices =
    (mediaDevices) => (dispatch, getState) => {
        const { phone, headsets } = getState();
        dispatch(receiveConnectedMediaDevices(mediaDevices));

        const phoneAudioReference = phone.get('audioReference');

        const preferredAudioOutputId =
            headsets.get('preferredAudioOutputId') ||
            getPreferredAudioOutputId();
        const preferredAudioOutput = getConnectedMediaDeviceOfKindWithId(
            mediaDevices,
            'audiooutput',
            preferredAudioOutputId,
        );

        const preferredAdditionalRingtoneAudioOutputId =
            headsets.get('preferredRingtoneAudioOutputId') ||
            getPreferredRingtoneAudioOutputId();
        const additionalRingtoneAudioOutput =
            getConnectedMediaDeviceOfKindWithId(
                mediaDevices,
                'audiooutput',
                preferredAdditionalRingtoneAudioOutputId,
            );

        if (preferredAudioOutput) {
            ToneGenerator.setOutputDevice(preferredAudioOutput.deviceId);
            setOutputDeviceWithIdToElement(
                preferredAudioOutput.deviceId,
                phoneAudioReference,
            );
            MainAudioContext.getPrimaryOutput().setOutputDevice(
                preferredAudioOutput.deviceId,
            );
        } else {
            const audioOutputDevices = filterMediaDevicesBySpecificKind(
                mediaDevices,
                'audiooutput',
            );

            if (audioOutputDevices && audioOutputDevices[0]) {
                MainAudioContext.getPrimaryOutput().setOutputDevice(
                    audioOutputDevices[0].deviceId,
                );
                ToneGenerator.setOutputDevice(audioOutputDevices[0].deviceId);
                setOutputDeviceWithIdToElement(
                    audioOutputDevices[0].deviceId,
                    phoneAudioReference,
                );
            }
        }

        if (additionalRingtoneAudioOutput) {
            MainAudioContext.getSecondaryOutput().setOutputDevice(
                additionalRingtoneAudioOutput.deviceId,
            );
        }

        // This is overkill. This should be handled once. Search for this comment for dubes.
        if (!additionalRingtoneAudioOutput) {
            MainAudioContext.getSecondaryOutput().deactivate();
        } else if (
            preferredAdditionalRingtoneAudioOutputId === preferredAudioOutputId
        ) {
            MainAudioContext.getSecondaryOutput().deactivate();
        } else {
            MainAudioContext.getSecondaryOutput().activate();
        }
    };

export const receiveConnectedMediaDevices = (connectedMediaDevices) => ({
    type: Actions.CONNECTED_MEDIA_DEVICES,
    connectedMediaDevices,
});

const logConnectedOutputDevices = (_dispatch, getState) => {
    const { headsets } = getState();

    const connectedMediaDevices = headsets.get('connectedMediaDevices');
    const preferredAudioOutputId =
        headsets.get('preferredAudioOutputId') || getPreferredAudioOutputId();
    const preferredRingtoneAudioOutputId =
        headsets.get('preferredRingtoneAudioOutputId') ||
        getPreferredRingtoneAudioOutputId();
    const audioOutputs = filterMediaDevicesBySpecificKind(
        connectedMediaDevices,
        'audiooutput',
    );
    const devicesString = getMediaDevicesLogString(audioOutputs);

    addDeviceLogEntry(
        'Output media devices:\r\n' +
            devicesString +
            '\r\n' +
            '---\r\n' +
            'Preferred output device: ' +
            preferredAudioOutputId +
            '\r\n' +
            'Extra ringtone device: ' +
            preferredRingtoneAudioOutputId,
    );
};

const logConnectedInputDevices = (_dispatch, getState) => {
    const { headsets } = getState();

    const connectedMediaDevices = headsets.get('connectedMediaDevices');
    const preferredMicrophoneId = headsets.get('preferredMicrophoneId');
    const audioInputs = filterMediaDevicesBySpecificKind(
        connectedMediaDevices,
        'audioinput',
    );
    const devicesString = getMediaDevicesLogString(audioInputs);

    addDeviceLogEntry(
        'Input media devices:\r\n' +
            devicesString +
            '\r\n' +
            '---\r\n' +
            'Preferred microphone device: ' +
            preferredMicrophoneId,
    );
};

export const updateUserStatusFromState = () => (dispatch, getState) => {
    dispatch(updateUserStatus(getLoggedInUserExtensionStatus(getState())));
};

export const updateUserStatus = (status) => (dispatch) => {
    switch (status) {
        case 'available':
            electronActions.sendUserAvailable();
            dispatch(hsUserAvailable());
            break;
        case 'ringingCall':
            electronActions.sendUserPendingBusy();
            break;
        case 'inCall':
        case 'inExternalCall':
            electronActions.sendUserBusy();
            break;
        case 'unavailable':
            electronActions.sendUserUnavailable();
            dispatch(hsUserUnavailable());
            break;
        case 'unknown':
            electronActions.sendUserAvailable();
            break;
        default:
            electronActions.sendUserBusy();
            break;
    }
};
